import { getConfig } from "@bidbattles/bloks/config";
import { isNotProduction } from "@bidbattles/bloks/hooks/dev";
import { Result } from "@bidbattles/bloks/lib/result";
import { z } from "zod/v4";

export type BackendEndpoint =
	// Auth
	| { method: "POST"; endpoint: "/v1/signup" }
	| { method: "POST"; endpoint: "/v1/login" }
	// Battles
	| { method: "GET"; endpoint: "/v1/battles" }
	| { method: "GET"; endpoint: `/v1/battles/${string}` }
	| { method: "GET"; endpoint: "/v1/categories" }
	| { method: "GET"; endpoint: "/v1/tags" }
	// Bid packages and prices
	| { method: "GET"; endpoint: `/v1/bids/price/${string}` }
	| { method: "GET"; endpoint: `/v1/bids/package/${string}` }
	// Payments & Subscriptions
	| { method: "GET"; endpoint: "/v1/subscriptions" }
	| { method: "GET"; endpoint: "/v1/payments" }
	| { method: "GET"; endpoint: `/v1/payments/${string}` }
	| { method: "POST"; endpoint: "/v1/payments/checkout" }
	// User
	| { method: "GET"; endpoint: "/v1/user" }
	| { method: "GET"; endpoint: "/v1/user/profile" }
	| { method: "GET"; endpoint: "/v1/loyalty/balance" }
	| { method: "GET"; endpoint: "/v1/loyalty/transactions" }
	| { method: "GET"; endpoint: "/v1/referral/code" }
	| { method: "POST"; endpoint: "/v1/user/profile" }
	| { method: "POST"; endpoint: "/v1/user/update_password" }
	| { method: "POST"; endpoint: "/v1/user/send_reset_password_token" }
	| { method: "POST"; endpoint: "/v1/user/reset_password" }
	// Notifications
	| { method: "GET"; endpoint: "/v1/notifications" }
	| { method: "GET"; endpoint: "/v1/notifications/preferences" }
	| { method: "POST"; endpoint: "/v1/notifications/preferences" }
	| { method: "POST"; endpoint: "/v1/notifications/subscribe" }
	| { method: "PATCH"; endpoint: `/v1/notifications/${string}/read` }
	// Admin
	| { method: "GET"; endpoint: "/v1/admin/items" }
	| { method: "GET"; endpoint: `/v1/admin/item/${string}` }
	| { method: "GET"; endpoint: "/v1/admin/users" }
	| { method: "GET"; endpoint: `/v1/admin/users/${string}` }
	| { method: "GET"; endpoint: `/v1/admin/users/${string}/profile` }
	| { method: "GET"; endpoint: `/v1/admin/battles/${string}/participants` }
	| { method: "GET"; endpoint: `/v1/admin/users/${string}/transaction_history` }
	| { method: "GET"; endpoint: "/v1/admin/bids/types" }
	| { method: "GET"; endpoint: "/v1/admin/bids/prices" }
	| { method: "GET"; endpoint: "/v1/admin/bids/packages" }
	| { method: "POST"; endpoint: "/v1/admin/battles" }
	| { method: "POST"; endpoint: "/v1/admin/items" }
	| { method: "POST"; endpoint: "/v1/admin/categories" }
	| { method: "POST"; endpoint: "/v1/admin/tags" }
	| { method: "POST"; endpoint: "/v1/admin/bids/type" }
	| { method: "POST"; endpoint: "/v1/admin/bids/price" }
	| { method: "POST"; endpoint: "/v1/admin/bids/package" }
	| { method: "POST"; endpoint: `/v1/admin/battles/${string}/status` }
	| { method: "POST"; endpoint: `/v1/admin/battles/${string}/participants` }
	| { method: "POST"; endpoint: `/v1/admin/payments/reconcile` }
	| { method: "POST"; endpoint: `/v1/admin/users/${string}/loyalty` }
	| { method: "POST"; endpoint: `/v1/admin/users/${string}/subscription` }
	| { method: "PATCH"; endpoint: `/v1/admin/users/${string}/role` }
	| { method: "PATCH"; endpoint: `/v1/admin/items/${string}` }
	| { method: "PATCH"; endpoint: `/v1/admin/battles/${string}` }
	| { method: "PATCH"; endpoint: `/v1/admin/bids/price/${string}` }
	| { method: "PATCH"; endpoint: `/v1/admin/bids/package/${string}` }
	| { method: "DELETE"; endpoint: `/v1/admin/items/${string}` }
	| { method: "DELETE"; endpoint: `/v1/admin/battles/${string}` }
	| { method: "DELETE"; endpoint: `/v1/admin/battles/${string}/participants` };

export class BBBackendError extends Error {}

export type SearchParams = string[][];
export function toSearchParams(params: Record<string, unknown>): SearchParams {
	const transformed = Object.entries(params)
		.filter(([_, value]) => value !== undefined && value !== null)
		.flatMap(([field, value]) => [[field, String(value)]]);
	return transformed;
}

export function parseCookies(strs: string[]) {
	return strs
		.map((v) => v.replace(/;.*$/, "").split("="))
		.reduce((acc, v) => {
			acc.set(decodeURIComponent(v[0].trim()), decodeURIComponent(v[1].trim()));
			return acc;
		}, new Map<string, string>());
}

export type BaseURLs = { apiURL: string; rtURL: string; rtHttpURL: string };
type URLKeys = keyof BaseURLs;
export type BaseURLsWithOverride = BaseURLs & { overriden: boolean };
const cookieNames = {
	apiURL: "rapace:api_url",
	rtURL: "rapace:rt_url",
	rtHttpURL: "rapace:rt_http_url",
} as const;

function getBaseURLsFromCookie(cookie: string | null): BaseURLsWithOverride {
	let overriden = false;
	let apiURL = Result.unwrap(getConfig().apiURL);
	let rtURL = Result.unwrap(getConfig().rtURL);
	let rtHttpURL = Result.unwrap(getConfig().rtHttpURL);

	if (cookie && isNotProduction(getConfig().env)) {
		const cookies = parseCookies(cookie.split("; "));
		let url = cookies.get(cookieNames.apiURL);
		if (url) {
			overriden = true;
			apiURL = url;
		}

		url = cookies.get(cookieNames.rtURL);
		if (url) {
			overriden = true;
			rtURL = url;
		}

		url = cookies.get(cookieNames.rtHttpURL);
		if (url) {
			overriden = true;
			rtHttpURL = url;
		}
	}

	return { overriden, apiURL, rtURL, rtHttpURL };
}

export function getBaseURLs() {
	let cookie: string | null = null;
	if (getConfig().isServer) {
		// This can never fail on the server
		cookie = getConfig().getCookie__ServerOnly();
	} else {
		cookie = document.cookie;
	}
	return getBaseURLsFromCookie(cookie);
}

// CookieStore APIs are only available client-side
export async function clearBaseURLOverrides__ClientOnly(): Promise<
	Result<null>
> {
	return Result.flatten(
		await Result.tryAsync(async () => {
			if (getConfig().isServer) {
				return Result.error(new Error("ILLEGAL!!!"));
			}

			for (const name of Object.values(cookieNames)) {
				await cookieStore.delete({
					name,
					domain: location.hostname,
				});
			}

			return Result.ok(null);
		}),
	);
}

export async function overrideBaseURLs__ClientOnly(
	urls: BaseURLs,
): Promise<Result<null>> {
	return Result.flatten(
		await Result.tryAsync(async () => {
			if (getConfig().isServer) {
				return Result.error(new Error("ILLEGAL!!!"));
			}

			for (const url of Object.keys(cookieNames) as Array<URLKeys>) {
				if (urls[url].trim() !== "") {
					await cookieStore.set({
						name: cookieNames[url],
						value: urls[url].trim(),
						domain: location.hostname,
					});
				}
			}

			return Result.ok(null);
		}),
	);
}

async function coreFetch({
	method,
	endpoint,
	query,
	...options
}: BackendEndpoint &
	Omit<RequestInit, "method"> & {
		query?: URLSearchParams;
	}) {
	return await Result.tryAsync(async () => {
		const attachCookiesManually = getConfig().isServer;
		let cookie: string | null;
		if (getConfig().isServer) {
			cookie = getConfig().getCookie__ServerOnly();
		} else {
			cookie = document.cookie;
		}

		const baseURLs = getBaseURLsFromCookie(cookie);
		const url = new URL(`${baseURLs.apiURL}${endpoint}`);
		if (baseURLs.overriden) {
			console.info("[fetch] using overriden api url to fetch", {
				cookie,
				url: url.toString(),
			});
		}

		if (query) {
			url.search = query.toString();
		}
		const response = await fetch(url, {
			...options,
			method,
			headers: {
				...options?.headers,
				...(attachCookiesManually && !!cookie && { cookie }),
			},
			credentials: "include",
		});

		// if (!response.ok) {
		// 	if (response.status === 401) {
		// 		await config.logout().catch((error) => {
		// 			console.warn("[auth] deauth failed", { error });
		// 		});
		// 	}
		// }

		return response;
	});
}

type FormDataValue = string | number | Blob;
const toRawFormDataValue = (v: FormDataValue): string | Blob => {
	if (v instanceof Blob) {
		return v;
	}

	return String(v);
};

export async function fdFetch({
	body,
	// headers,
	...options
}: Exclude<BackendEndpoint, Extract<BackendEndpoint, { method: "GET" }>> &
	Omit<RequestInit, "body" | "method"> & {
		query?: URLSearchParams;
		body: Record<string, null | undefined | FormDataValue | FormDataValue[]>;
	}) {
	return Result.flatten(
		await Result.tryAsync(async () => {
			const fd = new FormData();
			for (const [key, value] of Object.entries(body)) {
				if (value === null || value === undefined) {
					continue;
				}

				if (Array.isArray(value)) {
					for (const valueItem of value) {
						fd.append(key, toRawFormDataValue(valueItem));
					}

					continue;
				}

				fd.set(key, toRawFormDataValue(value));
			}

			return await coreFetch({
				body: fd,
				// headers: { ...headers, "content-type": "multipart/form-data" },
				...options,
			});
		}),
	);
}

export async function jsonFetch({
	body,
	headers,
	...options
}: BackendEndpoint &
	Omit<RequestInit, "body" | "method"> & {
		query?: URLSearchParams;
		body?: Record<string, unknown>;
	}) {
	return Result.flatten(
		await Result.tryAsync(async () => {
			const jsonBody = body ? JSON.stringify(body) : body;
			return await coreFetch({
				body: jsonBody,
				headers: { ...headers, "content-type": "application/json" },
				...options,
			});
		}),
	);
}

const errorSchema = z.object({ message: z.string() });
const newErrorSchema = z.object({
	code: z.string(),
	message: z.string(),
	details: z
		.array(
			z.object({
				field: z.string(),
				reason: z.string(),
				value: z.string(),
			}),
		)
		.optional(),
});

export const paginationSearchParamsSchema = z.object({
	query: z.string().optional(),
	next: z.string().optional(),
	previous: z.array(z.string()).catch([]),
	limit: z.union([z.literal(1), z.literal(10), z.literal(25)]).catch(10),
});
export type PaginationSearchParams = z.infer<
	typeof paginationSearchParamsSchema
>;

const paginationSchema = z.object({
	has_next: z.boolean(),
	has_prev: z.boolean(),
	next_cursor: z.string(),
});
export type PageInfo = z.infer<typeof paginationSchema>;

export function createResponseSchema<TDataSchema extends z.ZodType>(
	schema: TDataSchema,
) {
	return z.union([
		z.object({ error: newErrorSchema }),
		errorSchema,
		z.object({ data: schema }),
	]);
}

export function createPaginatedResponseSchema<TDataSchema extends z.ZodType>(
	schema: TDataSchema,
) {
	return z.union([
		z.object({ error: newErrorSchema }),
		errorSchema,
		z.object({ data: z.array(schema), pagination: paginationSchema }),
	]);
}

type ResponseSchema<TDataSchema extends z.ZodType> =
	| ReturnType<typeof createResponseSchema<TDataSchema>>
	| ReturnType<typeof createPaginatedResponseSchema<TDataSchema>>;
type SuccessResult<
	TDataSchema extends z.ZodType,
	TResponse extends z.infer<ResponseSchema<TDataSchema>>,
> = Extract<TResponse, { data: unknown }>;

export async function parseResponse<
	TDataSchema extends z.ZodType,
	TResponseSchema extends ResponseSchema<TDataSchema>,
>(
	schema: TResponseSchema,
	response: object | Response,
): Promise<Result<SuccessResult<TDataSchema, z.infer<TResponseSchema>>>> {
	let raw: unknown = response;
	if (response instanceof Response) {
		const result = await Result.tryAsync(async () => {
			const text = await response.text();
			return JSON.parse(text);
		});
		if (!result.ok) {
			return result;
		}

		raw = result.data;
	}

	const parseResult = await schema.safeParseAsync(raw);
	if (!parseResult.success) {
		return Result.error(parseResult.error);
	}

	const data = parseResult.data;
	if ("message" in data) {
		let message = data.message;
		if (message.toLowerCase() === "failed to authenticate user") {
			message = "unauthorized";
		}

		return Result.error(new BBBackendError(message));
	}

	if ("error" in data) {
		let message = data.error.message;
		if (message.toLowerCase() === "failed to authenticate user") {
			message = "unauthorized";
		}

		return Result.error(new BBBackendError(message));
	}

	return Result.ok(data) as Result<
		SuccessResult<TDataSchema, z.infer<TResponseSchema>>
	>;
}
